<template>
  <section class="history" role="region" aria-labelledby="history-title">
    <div class="history__header">
      <div>
        <h2 id="history-title">Historial</h2>
        <p class="muted">Registro completo de mantenimientos y costes.</p>
      </div>
      <div class="history__filters">
        <label>
          Año
          <select v-model="yearFilter">
            <option value="">Todos</option>
            <option v-for="year in availableYears" :key="year" :value="String(year)">
              {{ year }}
            </option>
          </select>
        </label>
        <label>
          Coste
          <select v-model="costFilter">
            <option value="">Todos</option>
            <option value="low">0-100€</option>
            <option value="mid">100-300€</option>
            <option value="high">300€+</option>
          </select>
        </label>
      </div>
    </div>

    <div v-if="loading" class="history__list">
      <div v-for="n in 3" :key="`hist-skel-${n}`" class="history-item">
        <span class="history-dot skeleton"></span>
        <div class="history-card">
          <div class="skeleton skeleton-line" style="width: 70%"></div>
          <div class="skeleton skeleton-line" style="width: 50%"></div>
          <div class="skeleton skeleton-line" style="width: 60%"></div>
        </div>
      </div>
    </div>

    <div v-else class="history__list">
      <div v-for="group in groupedRecords" :key="group.year" class="history-group">
        <div class="history-year">{{ group.year }}</div>
        <div class="history-items">
          <article
            v-for="item in group.items"
            :key="item.id"
            class="history-item"
            :class="{ clickable }"
            role="button"
            tabindex="0"
            @click="handleClick(item)"
            @keydown.enter.prevent="handleClick(item)"
          >
            <span class="history-dot" aria-hidden="true"></span>
            <div class="history-card">
              <div class="history-card__header">
                <div>
                  <h3>{{ item.name }}</h3>
                  <p class="muted">{{ item.dateLabel }}</p>
                </div>
                <div class="history-card__meta">
                  <UiBadge tone="info">{{ item.costLabel }}</UiBadge>
                </div>
              </div>
              <p class="history-card__notes">{{ item.notesLabel }}</p>
              <div class="history-card__details">
                <div>
                  <span>Taller</span>
                  <strong>{{ item.workshopLabel }}</strong>
                </div>
                <div>
                  <span>Factura</span>
                  <strong>{{ item.invoiceLabel }}</strong>
                </div>
                <div>
                  <span>Documentos</span>
                  <strong>{{ item.docsLabel }}</strong>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>

      <div v-if="!groupedRecords.length" class="empty-card">
        <p>No hay mantenimientos todavía.</p>
      </div>
    </div>
  </section>
</template>

<script>
import UiBadge from "../ui/UiBadge.vue";

export default {
  name: "HistoryTimeline",
  components: { UiBadge },
  props: {
    records: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    clickable: { type: Boolean, default: false },
  },
  data() {
    return {
      yearFilter: "",
      costFilter: "",
    };
  },
  computed: {
    availableYears() {
      const years = new Set(
        this.records
          .map((item) => (item.performedAt ? new Date(item.performedAt).getFullYear() : null))
          .filter(Boolean)
      );
      return Array.from(years).sort((a, b) => b - a);
    },
    filteredRecords() {
      return this.records.filter((item) => {
        if (this.yearFilter) {
          const year = item.performedAt ? new Date(item.performedAt).getFullYear() : null;
          if (String(year) !== this.yearFilter) return false;
        }
        if (this.costFilter) {
          const cost = Number(item.costEur || 0);
          if (this.costFilter === "low" && cost > 100) return false;
          if (this.costFilter === "mid" && (cost < 100 || cost > 300)) return false;
          if (this.costFilter === "high" && cost < 300) return false;
        }
        return true;
      });
    },
    groupedRecords() {
      const groups = {};
      this.filteredRecords.forEach((item) => {
        const year = item.performedAt ? new Date(item.performedAt).getFullYear() : "—";
        if (!groups[year]) groups[year] = [];
        groups[year].push(item);
      });
      return Object.keys(groups)
        .sort((a, b) => Number(b) - Number(a))
        .map((year) => ({
          year,
          items: groups[year].map((item) => ({
            ...item,
            dateLabel: item.performedAtLabel || item.performedAt || "—",
            costLabel: item.costLabel || "—",
            notesLabel: item.description || "Sin notas",
            workshopLabel: item.workshop || "—",
            invoiceLabel: item.invoice || (item.tickets?.length ? "Factura adjunta" : "Sin factura"),
            docsLabel: item.tickets?.length ? `${item.tickets.length} archivos` : "0 archivos",
          })),
        }));
    },
  },
  methods: {
    handleClick(item) {
      if (!this.clickable) return;
      this.$emit("open", item);
    },
  },
};
</script>

<style scoped>
.history {
  display: grid;
  gap: 16px;
}

.history__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.history__header h2 {
  margin: 0 0 6px;
}

.history__header p {
  margin: 0;
}

.history__filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.history__filters label {
  display: grid;
  gap: 6px;
  font-size: 12px;
  color: var(--muted);
}

.history__filters select {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 10px;
  color: var(--text);
  min-width: 120px;
}

.history__list {
  display: grid;
  gap: 16px;
  position: relative;
  padding-left: 6px;
}

.history__list::before {
  content: "";
  position: absolute;
  left: 8px;
  top: 10px;
  bottom: 10px;
  width: 2px;
  background: rgba(148, 163, 184, 0.18);
}

.history-group {
  display: grid;
  gap: 12px;
}

.history-year {
  font-size: 14px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.history-items {
  display: grid;
  gap: 12px;
}

.history-item {
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 12px;
  align-items: start;
}

.history-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.45);
  margin-top: 14px;
}

.history-card {
  background: var(--color-card);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px;
  display: grid;
  gap: 12px;
  box-shadow: var(--shadow);
}

.history-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.history-card__header h3 {
  margin: 0;
}

.history-card__meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.history-card__notes {
  margin: 0;
  color: var(--muted);
}

.history-card__details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  font-size: 13px;
}

.history-card__details span {
  color: var(--muted);
}

.history-card__details strong {
  display: block;
  margin-top: 4px;
}

.history-item.clickable .history-card {
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.history-item.clickable .history-card:hover {
  transform: translateY(-1px);
  border-color: rgba(56, 189, 248, 0.4);
}

.history-item.clickable .history-card:focus-visible {
  outline: 2px solid rgba(56, 189, 248, 0.7);
  outline-offset: 2px;
}
</style>
